import { action, makeObservable, override } from 'mobx';

import { FinalVisualizationBuilder } from '../configurator/VisualizationBuilder';
import { IApiClient } from '../data/client';
import type { BaseModelBasicData, FacilityInfoData } from '../data/model';
import {
  GetBasicProductByIdQuery,
  GetModularConfigurationByCodeQuery,
  GetModularProductFamilyQuery,
  GetProductConfigurationByCodeQuery,
  GetProductFacilityInfoQuery,
  ModularComponentConfigurationData,
  ModularConfigurationData,
  ModularProductFamilyData,
} from '../data/model';
import type { RequestQuoteFormConfig } from '../data/model';
import { HubSpotPageTranslation } from '../localization/SiteTranslation';
import { HubSpotFormState, IHubSpotFormMemento } from '../shared/hubSpot/HubSpotFormState';
import { INavigationService } from '../shared/NavigationService';
import { IShareModularConfiguratorUrlProvider } from '../shared/ShareModularConfiguratorUrlProvider';
import { StoreUrl } from '../shared/StoreUrl';
import { StoreState } from '../StoreState';
import { IConfigurationComponentState } from './components/IConfigurationComponentState';
import { ModularConfigurationRequestQuotePageQuery } from './ModularConfigurationRequestQuotePageQuery';
import { ModularConfigurationRequestQuotePageQueryParser } from './ModularConfigurationRequestQuotePageQueryParser';

export interface IModularConfigurationRequestQuoteMemento extends IHubSpotFormMemento<RequestQuoteFormConfig> {
  components: IConfigurationComponentState[];
  configuration: ModularConfigurationData;
  productFamily: ModularProductFamilyData;
  facilityInfo?: FacilityInfoData;
}

export class ModularConfigurationRequestQuotePageState extends HubSpotFormState<
  RequestQuoteFormConfig,
  IModularConfigurationRequestQuoteMemento
> {
  public components: IConfigurationComponentState[] = [];

  public readonly query: ModularConfigurationRequestQuotePageQuery;

  private productFamily: ModularProductFamilyData;
  private configuration?: ModularConfigurationData;
  private facilityInfo?: FacilityInfoData;

  public get brand() {
    // TODO: Replace the hard-coded brand with a value from GetModularProductFamilyQuery if available
    return 'profim';
  }

  public get configurationCode() {
    return this.query.code;
  }

  public get isEmbedded() {
    return this.query.embed;
  }

  public get isRequestProcessedByDealer() {
    return this.hubSpotConfig.isProcessedByDealer;
  }

  public get layoutId() {
    return this.configuration?.layoutId ?? '';
  }

  public get productFamilyId() {
    return this.productFamily.id;
  }

  public get productName() {
    return this.productFamily.name;
  }

  public get shopUrl() {
    return this.configurationCode ? this.shareConfiguratorUrlProvider.getUrl(this.configurationCode) : '';
  }

  public get facilityName() {
    return this.facilityInfo?.facilityName ?? '';
  }

  public get facilityId() {
    return this.facilityInfo?.facilityNumber ?? '';
  }

  public constructor(
    client: IApiClient,
    navigation: INavigationService,
    pageTranslation: HubSpotPageTranslation,
    private readonly shareConfiguratorUrlProvider: IShareModularConfiguratorUrlProvider,
  ) {
    super(client, navigation, pageTranslation);

    makeObservable(this);

    this.query = this.parseQuery(navigation.currentUrl.query);
  }

  public async onLoad(store: StoreState): Promise<void> {
    await super.onLoad(store);

    const configurationCode = this.configurationCode;

    if (configurationCode) {
      const { modularConfigurationData } = await this.client.send(
        new GetModularConfigurationByCodeQuery({ modularCode: configurationCode }),
      );
      const productFamilyId = modularConfigurationData.productFamilyId;
      this.shareConfiguratorUrlProvider.initialize(productFamilyId);

      this.productFamily = await this.fetchProductFamilyData(productFamilyId);
      this.components = await this.fetchComponentsData(modularConfigurationData);
      this.configuration = modularConfigurationData;

      const facilityInfoResponse = await this.client.send(
        new GetProductFacilityInfoQuery({
          productId: this.configuration.components[0].productId,
        }),
      );
      this.facilityInfo = facilityInfoResponse?.facilityInfo;
    } else if (this.query.familyId) {
      this.productFamily = await this.fetchProductFamilyData(this.query.familyId);
    }
  }

  public getMemento(): IModularConfigurationRequestQuoteMemento {
    return {
      ...super.getMemento(),
      components: this.components,
      configuration: this.configuration,
      productFamily: this.productFamily,
      facilityInfo: this.facilityInfo,
    };
  }

  public restoreMemento(memento: IModularConfigurationRequestQuoteMemento) {
    super.restoreMemento(memento);
    this.components = memento.components;
    this.configuration = memento.configuration;
    this.productFamily = memento.productFamily;
    this.facilityInfo = memento.facilityInfo;
  }

  @action.bound
  public backToSummary(): void {
    this.navigation.back();
  }

  @override
  public setFormSubmitted() {
    super.setFormSubmitted();
  }

  protected ensureFeatureIsEnabled(store: StoreState) {
    if (!store.canRequestQuote()) {
      this.navigation.navigateTo(StoreUrl.homeUrl());
    }
  }

  protected hubSpotConfigSelector(store: StoreState) {
    return store.storeResponse.storeContext.requestQuoteConfig;
  }

  protected parseQuery(rawQuery: Map<string, string>): ModularConfigurationRequestQuotePageQuery {
    return ModularConfigurationRequestQuotePageQueryParser.toModel(rawQuery);
  }

  private async fetchComponentsData(configurationData: ModularConfigurationData) {
    const configurationProductsData = await this.fetchProductsData(configurationData.components);

    const components: IConfigurationComponentState[] = [];

    for (const { productId, productConfigurationCode } of configurationData.components) {
      const component = components.find((x) => x.configurationCode === productConfigurationCode);

      if (component) {
        component.counter++;
        continue;
      }

      const { product } = configurationProductsData.find((x) => x.product.id === productId);
      const componentData = await this.getComponentData(productConfigurationCode, product);

      components.push(componentData);
    }

    return components;
  }

  private async fetchProductsData(components: ModularComponentConfigurationData[]) {
    const uniqueProductIds = components.reduce<string[]>((result, { productId }) => {
      if (productId && !result.includes(productId)) {
        result.push(productId);
      }
      return result;
    }, []);

    return Promise.all(
      uniqueProductIds.map((productId) =>
        this.client.send(new GetBasicProductByIdQuery({ productId, shoppingContext: this.shoppingContext })),
      ),
    );
  }

  private async fetchProductFamilyData(productFamilyId: string) {
    const { productFamily } = await this.client.send(
      new GetModularProductFamilyQuery({
        productFamilyId,
        shoppingContext: this.shoppingContext,
      }),
    );
    return productFamily;
  }

  private async getComponentData(componentConfigurationCode: string, productData: BaseModelBasicData) {
    const productConfiguration = await this.client.send(
      new GetProductConfigurationByCodeQuery({
        code: componentConfigurationCode,
        shoppingContext: this.shoppingContext,
      }),
    );
    const imageUrl =
      productData.images.first()?.url ||
      productData.outline.url ||
      FinalVisualizationBuilder.buildUrl(componentConfigurationCode);

    return {
      counter: 1,
      configurationCode: componentConfigurationCode,
      displayName: productData.displayName,
      imageUrl,
      accessories: productConfiguration.accessories,
      options: productConfiguration.options,
    };
  }
}
